import QuantitySelect from '@/components/QuantitySelect'
import { DeleteOutlined } from '@mui/icons-material'
import CommentIcon from '@mui/icons-material/Comment'
import ExpandCircleDownIcon from '@mui/icons-material/ExpandCircleDown'
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight'
import { Box, Collapse, Divider, IconButton, Stack, Typography } from '@mui/material'

import React from 'react'

import type { Item } from './index'
import { ItemNotePopover } from './item-note-popover'

type Props = {
  onDeleteClick: (itemId: string) => void
  onQuantityChange: (itemId: string, quantity: number) => void
  onNoteChange: (itemId: string, note: string) => void
  showItemNotes: boolean
  isUnavailable?: boolean // Add prop for unavailable status
} & Item

const BasketItem: React.FC<Props> = ({
  onDeleteClick,
  onQuantityChange,
  onNoteChange,
  showItemNotes,
  isUnavailable = false,
  ...item
}) => {
  const { id, itemOptions, price, name, note, minimumQuantity, quantity, updatedByVendor } = item
  const [expanded, setExpanded] = React.useState(false)

  const handleExpandOptionOrNoteClick = () => {
    setExpanded(!expanded)
  }

  const handleOnQuantityChange = (newQuantityValue: number) => {
    onQuantityChange(id, newQuantityValue)
  }

  // Custom items have no itemReference in basket.items, so
  // updateItemInBasket can't target them — notes are read-only. canUpdateQty
  // mirrors the backend's group-order contributor check (userCannotEditGroupOrderLine),
  // so a contributor can't open an editor the mutation would reject anyway.
  const canEditNoteInline = showItemNotes && !item.isCustomItem && item.canUpdateQty
  const hasExpandableContent = itemOptions.length > 0 || (!canEditNoteInline && note.length > 0)

  const getTextColor = () => {
    if (isUnavailable) return 'warning.main'
    if (updatedByVendor) return 'warning.main'
    return 'text.primary'
  }

  const getBackgroundColor = () => {
    if (isUnavailable) return 'warning.50'
    return 'transparent'
  }

  return (
    <Stack
      spacing={1}
      mb={1}
      sx={{
        backgroundColor: getBackgroundColor(),
        borderRadius: isUnavailable ? 1 : 0,
        border: isUnavailable ? '1px solid' : 'none',
        borderColor: isUnavailable ? 'warning.main' : 'transparent',
        padding: isUnavailable ? 1 : 0,
        opacity: isUnavailable ? 0.8 : 1,
      }}>
      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Typography color={getTextColor()} variant="body3" fontWeight={600}>
          {name}
        </Typography>
        <Typography variant="body3" fontWeight={600} color={getTextColor()}>
          {price}
        </Typography>
      </Stack>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        {itemOptions?.map((option, index) => {
          const { name, priceExTax } = option
          const key = `${name}-${index}`
          return (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              key={key}>
              <Box display="flex" alignItems="center">
                <SubdirectoryArrowRightIcon sx={{ transform: 'scale(0.5)' }} />
                <Typography variant="caption" sx={{ marginLeft: 1 }}>
                  {name} x{option.qty}
                </Typography>
              </Box>
              <Typography variant="caption">{priceExTax}</Typography>
            </Stack>
          )
        })}
        {!canEditNoteInline && note && (
          <Stack direction={'row'} alignItems={'center'}>
            <CommentIcon sx={{ transform: 'scale(0.5)' }} />{' '}
            <Typography paddingLeft={1} variant="caption">
              {note}
            </Typography>
          </Stack>
        )}
      </Collapse>
      <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
        <QuantitySelect
          onChange={handleOnQuantityChange}
          max={!item.canUpdateQty ? quantity : 2000}
          min={!item.canUpdateQty ? quantity : minimumQuantity}
          size="small"
          quantity={quantity}
          disabled={!item.canUpdateQty}
          decreaseDataCy="cart-item-decrease"
          increaseDataCy="cart-item-incrementer"
        />
        {canEditNoteInline && (
          <ItemNotePopover
            note={note}
            onSave={(newNote) => onNoteChange(id, newNote)}
            onRemove={() => onNoteChange(id, '')}
          />
        )}
        {hasExpandableContent && (
          <IconButton
            sx={{ transform: `scale(0.75) ${expanded ? 'rotate(180deg)' : 'rotate(0deg)'}` }}
            onClick={handleExpandOptionOrNoteClick}>
            <ExpandCircleDownIcon />
          </IconButton>
        )}
        {onDeleteClick && (
          <IconButton
            disabled={!item.canDelete}
            style={{ margin: 0, padding: 0 }}
            onClick={() => onDeleteClick(id)}
            size="small">
            <DeleteOutlined />
          </IconButton>
        )}
      </Stack>
      <Divider />
    </Stack>
  )
}

export default BasketItem
